/**
 * MainLayout - Layout principal de la aplicación
 *
 * QUÉ HACE:
 * - Renderiza el Sidebar y el área de contenido principal
 * - Maneja la responsividad del sidebar (mobile overlay)
 * - Muestra header mobile con título de página dinámico
 * - Sincroniza permisos periódicamente via useAuthSync
 *
 * QUÉ INVOCA: Sidebar, useAuthSync
 * QUÉ EXPORTA: MainLayout (default)
 *
 * QUÉ NO DEBE HACER:
 * - NO debe contener lógica de autenticación directa
 * - NO debe hacer llamadas a API (delegado a useAuthSync)
 * - NO debe duplicar la lógica de títulos que ya existe en navigation.config.ts
 */

import { Suspense, lazy, useState, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import LoadErrorScreen from "@/components/common/LoadErrorScreen";
import {
  bumpNavEpoch,
  clearPageLoadFailure,
  isReloadLoopDetected,
  recordReloadAttempt,
  resetReloadLoopGuard,
} from "@/lib/pageLoadError";
import { usePageLoadErrorStore } from "@/store/pageLoadErrorStore";
import Sidebar from "../navigation/Sidebar";
import { Menu } from "lucide-react";
import { useAuthSync, useGlobalEscKey, useMesaOrderAlerts } from "@/hooks";
import { usePedidoAutoPrintWatcher } from "@/features/tienda/hooks/usePedidoAutoPrintWatcher";
import { NAV_ITEMS } from "../navigation/navigation.config";
import {
  getMetricsSection,
  isMetricsSectionId,
} from "@/features/metrics/services/metricsSections.service";
import PendingCierreWhatsAppOverlay from "@/features/caja/components/PendingCierreWhatsAppOverlay";
import { PrinterUnavailableModal } from "@/features/caja/components/PrinterUnavailableModal";
import { useCajaDemoMay12Seed } from "@/features/caja/hooks/useCajaDemoMay12Seed";
import ConfirmationModal from "@/components/common/ConfirmationModal";
import DemoModeBanner from "@/components/common/DemoModeBanner";
import OfflineNoticeOverlay from "@/components/common/OfflineNoticeOverlay";
import StorageQuotaMonitor from "@/components/common/StorageQuotaMonitor";
import { useNavigationGuardStore } from "@/store/navigationGuardStore";
import {
  unlockAudio,
  unlockSpeech,
  preloadVoices,
} from "@/lib/audio/appSounds";
import { SAFE_AREA } from "@/lib/nativeShell";
import { isDesktop } from "@/lib/platform";
import { RouteOutletFallback } from "@/components/common/routeFallback/RouteOutletFallback";
import MobileBottomNavigation from "@/components/navigation/MobileBottomNavigation";
import MobileOrientationLock from "@/components/navigation/MobileOrientationLock";

/**
 * Copilot global perezoso: mantiene el motor de negocio y el chat fuera del
 * chunk principal (solo se descarga cuando el layout monta, sin bloquear la
 * primera pintura).
 */
const GlobalCopilot = lazy(() =>
  import("@/components/copilot/GlobalCopilot").then((module) => ({
    default: module.GlobalCopilot,
  })),
);

const MainLayout = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();
  const { isBlocked, confirm, cancel } = useNavigationGuardStore();
  const pageLoadErrorActive = usePageLoadErrorStore((s) => s.active);

  const closeSidebar = () => setIsSidebarOpen(false);

  useEffect(() => {
    // Invalida reportes de error pendientes de la ruta anterior y oculta el overlay.
    bumpNavEpoch();
    clearPageLoadFailure();
  }, [location.pathname]);

  // Desbloquea audio y voz en el primer gesto (política de autoplay) para que
  // los avisos globales de pedidos (beep + voz) suenen sin interacción previa.
  useEffect(() => {
    preloadVoices();
    const unlock = () => {
      unlockAudio();
      unlockSpeech();
    };
    window.addEventListener("pointerdown", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, []);

  // Sincronización periódica de permisos (extraído a hook reutilizable)
  useAuthSync();
  /** Preset demo caja (12 may 2026) — idempotente; solo hidrata si aún no se aplicó. */
  useCajaDemoMay12Seed();
  /** Auto-impresión global de pedidos de tienda (independiente de la ruta). */
  usePedidoAutoPrintWatcher();
  /** Aviso global de pedidos de mesa nuevos (beep + voz + notificación). */
  useMesaOrderAlerts();

  // En móvil: Escape cierra el sidebar cuando está abierto
  useGlobalEscKey(closeSidebar, isSidebarOpen && !isDesktop);

  // Derivar título de la ruta actual usando navigation.config (fuente única de verdad)
  const getPageTitle = (): string => {
    const path = location.pathname;
    // Métricas: el header refleja la sección activa (`/metricas/<seccion>`).
    if (path === "/metricas") return "Métricas";
    // Página completa de análisis de una tarjeta (`/metricas/analisis/<cardId>`).
    if (path.startsWith("/metricas/analisis/")) return "Análisis";
    if (path.startsWith("/metricas/")) {
      const sectionId = path.slice("/metricas/".length);
      if (isMetricsSectionId(sectionId)) {
        return getMetricsSection(sectionId)?.label ?? "Métricas";
      }
    }
    // Coincidencia exacta primero
    for (const item of NAV_ITEMS) {
      if (item.path === path) return item.label;
      if (item.children) {
        for (const child of item.children) {
          if (child.path === path) return child.label;
        }
      }
    }
    // Coincidencia por prefijo para sub-rutas (ej. /higiene/:areaId → "Higiene")
    let best = { label: "Altenot", length: 0 };
    for (const item of NAV_ITEMS) {
      if (
        item.path &&
        path.startsWith(item.path + "/") &&
        item.path.length > best.length
      ) {
        best = { label: item.label, length: item.path.length };
      }
      if (item.children) {
        for (const child of item.children) {
          if (
            child.path &&
            path.startsWith(child.path + "/") &&
            child.path.length > best.length
          ) {
            best = { label: child.label, length: child.path.length };
          }
        }
      }
    }
    return best.label;
  };

  return (
    <div
      className="flex bg-white dark:bg-slate-900 transition-colors duration-200"
      style={{ minHeight: "100vh", height: "100%" }}
    >
      <MobileOrientationLock />
      {/* Overlay: entrar ~300ms, salir rápido (~150ms) como el drawer */}
      <button
        type="button"
        aria-label={isSidebarOpen ? "Cerrar menú" : undefined}
        className={`fixed inset-0 z-[45] border-0 bg-black lg:hidden dark:bg-black ${
          isSidebarOpen
            ? "pointer-events-auto cursor-pointer opacity-50 transition-opacity duration-300 ease-out dark:opacity-70"
            : "pointer-events-none cursor-default opacity-0 transition-opacity duration-150 ease-in"
        }`}
        onClick={closeSidebar}
        tabIndex={isSidebarOpen ? 0 : -1}
      />

      {/* Sidebar */}
      <Sidebar isOpen={isSidebarOpen} onClose={closeSidebar} />

      {/* Contenido principal */}
      <div className="relative flex min-w-0 w-full flex-1 flex-col overflow-hidden">
        <DemoModeBanner />
        {/* Header Móvil */}
        <header
          className={`${SAFE_AREA.mobileHeader} relative z-30 flex shrink-0 items-center gap-3 border-b border-gray-200 bg-white px-3 dark:border-slate-700 dark:bg-slate-900 lg:hidden sm:gap-4 sm:px-4`}
        >
          <button
            type="button"
            onClick={() => setIsSidebarOpen((open) => !open)}
            aria-label={isSidebarOpen ? "Cerrar menú" : "Abrir menú"}
            aria-expanded={isSidebarOpen}
            className="relative z-40 flex h-11 w-11 shrink-0 touch-manipulation items-center justify-center rounded-lg text-gray-600 transition-colors duration-150 hover:bg-gray-100 active:bg-gray-200 dark:text-gray-300 dark:hover:bg-slate-800 dark:active:bg-slate-700"
          >
            <Menu className="h-6 w-6" strokeWidth={2} aria-hidden />
          </button>
          <h1 className="min-w-0 flex-1 truncate text-lg font-bold text-gray-800 dark:text-gray-100 sm:text-xl">
            {getPageTitle()}
          </h1>
        </header>

        {/* Header Desktop - Eliminado para evitar duplicidad de títulos */}
        {/* El título se maneja individualmente en cada página */}

        {/* Área de contenido — scrollbar-mobile oculta el scroll feo en Android */}
        <main
          className={`${SAFE_AREA.mainScroll} ${SAFE_AREA.mainScrollMobileNav} relative flex-1 overflow-y-auto overflow-x-hidden scrollbar-mobile`}
        >
          <Suspense fallback={<RouteOutletFallback />}>
            <Outlet />
          </Suspense>
        </main>
      </div>

      <MobileBottomNavigation />

      {/* Copilot global: FAB + chat a pantalla completa (solo dueño/admin con
          acceso a `/ia`; oculto en Caja, Mesas, IA y análisis de Métricas). */}
      <Suspense fallback={null}>
        <GlobalCopilot />
      </Suspense>

      {/* Guardia global: modal de confirmacion al salir con carrito o datos sin guardar */}
      <ConfirmationModal
        isOpen={isBlocked}
        onClose={cancel}
        onConfirm={confirm}
        title="¿Salir de la caja?"
        message="Tienes productos en el carrito. Si sales ahora los perderás."
        variant="warning"
        confirmText="Sí, salir"
        cancelText="Quedarme aquí"
      />

      {/* Guardia global anti-fraude: bloquea la app si hay un cierre de caja
          pendiente de enviar por WhatsApp. Vive aquí (no en CajaPage) para que
          el empleado no pueda esquivarlo cambiando de ruta. */}
      <PendingCierreWhatsAppOverlay />

      <OfflineNoticeOverlay />
      <StorageQuotaMonitor />
      {/* Compuerta global: aviso/confirmación al imprimir sin impresora conectada. */}
      <PrinterUnavailableModal />

      {pageLoadErrorActive
        ? (() => {
            const loop = isReloadLoopDetected();
            return (
              <LoadErrorScreen
                variant="app"
                buttonLabel={loop ? "Continuar sin recargar" : undefined}
                message={
                  loop
                    ? "Seguimos sin poder contactar el servidor.\nPuedes continuar con los datos guardados en el dispositivo."
                    : undefined
                }
                footNote={
                  loop
                    ? "Tus datos locales están a salvo. No se borró nada."
                    : null
                }
                onRetry={() => {
                  // Cortacircuitos: si ya recargamos demasiadas veces, NO recargar
                  // (rompe el bucle infinito). Continuar con datos locales.
                  if (isReloadLoopDetected()) {
                    resetReloadLoopGuard();
                    clearPageLoadFailure();
                    return;
                  }
                  recordReloadAttempt();
                  window.location.reload();
                }}
              />
            );
          })()
        : null}
    </div>
  );
};

export default MainLayout;
